import { StyleableButton } from '@thanx/uikit/button'
import { Input } from '@thanx/uikit/input'
import { Radio, RadioGroup } from '@thanx/uikit/radio'
import { Select } from '@thanx/uikit/select'
import { Switch } from '@thanx/uikit/switch'
import { useBrameIntegration } from 'api/integrationsConfig'
import FakeInput from 'components/Form/FakeInput'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import sortBy from 'lodash/sortBy'
import React, { useEffect, useState } from 'react'
import { getUrlValue } from 'scenes/Cms/helper'

export enum UrlType {
  NoLink = 'no_link',
  External = 'external',
  Inapp = 'inapp',
}

export type UrlValue = {
  inapp: string
  external: string
  fullUrl?: string
  type: UrlType
  authenticable: boolean
  queryParams?: string
}

type Props = {
  id: string
  type: 'button' | 'image' | 'text'
  url: UrlValue
  required?: boolean
  hasQueryParam?: boolean
  disabled?: boolean
  onUrlChange?: (url: UrlValue) => void
  fixedUrlType?: UrlType.External | UrlType.Inapp
}

const t = buildTranslate('cms.components.url_picker')

export type Url = {
  name: string
  link: string
  label?: string
  alt_label?: boolean
}

export const DefaultUrls: Url[] = [
  {
    name: 'home',
    link: 'thanx://home',
  },
  {
    name: 'about',
    link: 'thanx://about',
  },
  {
    name: 'account',
    link: 'thanx://account',
  },
  {
    name: 'earn',
    link: 'thanx://earn',
  },
  {
    name: 'locations',
    link: 'thanx://locations',
  },
  {
    name: 'nps',
    link: 'thanx://nps',
  },
  {
    name: 'notifications',
    link: 'thanx://notifications',
  },
  {
    name: 'registered_cards',
    link: 'thanx://registered_cards',
  },
  {
    name: 'rewards',
    link: 'thanx://rewards',
  },
  {
    name: 'support',
    link: 'thanx://support',
  },
  {
    name: 'more',
    link: 'thanx://drawer',
  },
]

export const OrderUrls: Url[] = [
  {
    name: 'menu',
    link: 'thanx://ordering-menu',
    alt_label: true,
  },
  {
    name: 'history',
    link: 'thanx://order-history',
  },
]

export const ExclusiveDealUrls: Url[] = [
  {
    name: 'shop',
    link: 'thanx://shop',
  },
]

export const GiftCardUrls: Url[] = [
  {
    name: 'gift_cards',
    link: 'thanx://manage-gift-cards',
  },
]

export const GameUrls: Url[] = [
  {
    name: 'game',
    link: 'thanx://game',
  },
]

// Meant to replace UrlPicker which relies on Formsy. Which uses anti-patterns of hidden updates and unexpected side-effects.
// Please stop using Formsy for form handling
export const UrlPickerGeneric: React.FC<Props> = props => {
  const {
    id,
    url,
    required = true,
    hasQueryParam,
    disabled = false,
    onUrlChange,
    fixedUrlType,
  } = props
  const externalUrl = url.external || ''
  const fullUrl = url.fullUrl || externalUrl
  const { app_ordering_enabled, exclusive_deals, gift_cards_enabled } =
    useCurrentMerchant() || {}

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)
  const [urlValue, setUrlValue] = useState(externalUrl)
  const [authenticable, setAuthenticable] = useState(url.authenticable)
  const [urlType, setUrlType] = useState(fixedUrlType || url.type)
  const [inappValue, setInappValue] = useState(url.inapp || '')
  const giftCardsAvailable =
    useFlag('gift-cards-mx', false) && gift_cards_enabled
  const brameFeatureFlagEnabled = useFlag(
    'gamification-bottom-nav-enabled',
    false
  )
  const { brameIntegrationEnabled } = useBrameIntegration()
  const gameUrlsAvailable = brameFeatureFlagEnabled && brameIntegrationEnabled

  const urlOptions = getAvailableUrls()

  function getAvailableUrls() {
    let availableUrls: Url[] = DefaultUrls

    if (exclusive_deals) {
      availableUrls = availableUrls.concat(ExclusiveDealUrls)
    }

    if (app_ordering_enabled) {
      availableUrls = availableUrls.concat(OrderUrls)
    }

    if (giftCardsAvailable) {
      availableUrls = availableUrls.concat(GiftCardUrls)
    }

    if (gameUrlsAvailable) {
      availableUrls = availableUrls.concat(GameUrls)
    }

    return sortBy(
      availableUrls.map(url => {
        const translationKey =
          hasQueryParam && url.alt_label ? `${url.name}_alt` : url.name
        const translatedLabel = t(translationKey)

        return {
          label:
            translatedLabel ||
            url.name.charAt(0).toUpperCase() + url.name.slice(1), // Fallback to capitalized name if translation is missing
          value: url.link,
        }
      }),
      ['label']
    )
  }

  useEffect(() => {
    setUrlValue(externalUrl)
  }, [externalUrl])

  useEffect(() => {
    setUrlType(fixedUrlType || url.type)
  }, [url.type, fixedUrlType])

  useEffect(() => {
    setInappValue(url.inapp || '')
  }, [url.inapp])

  useEffect(() => {
    if (fixedUrlType && url.type !== fixedUrlType) {
      updateUrl({ type: fixedUrlType })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedUrlType])

  function removeLink() {
    setUrlValue('')
    setAuthenticable(false)
    updateUrl({ ...url, external: '', authenticable: false })
  }

  function updateUrl(updatedUrl: Partial<UrlValue>) {
    if (onUrlChange) {
      onUrlChange({ ...url, ...updatedUrl })
    }
  }

  function handleUrlTypeChange(newType: UrlType) {
    if (disabled) return
    setUrlType(newType)
    updateUrl({ type: newType })
  }

  function handleExternalUrlChange(newValue: string) {
    if (disabled) return
    setUrlValue(newValue)
    updateUrl({ external: newValue })
  }

  function handleInappUrlChange(
    newValue: string | string[] | Array<{ label: string; value: string }>
  ) {
    if (disabled) return

    let value = ''

    if (Array.isArray(newValue)) {
      if (newValue.length > 0) {
        // Handle array of objects (from Select component)
        if (typeof newValue[0] === 'object' && newValue[0] !== null) {
          value = (newValue[0] as { label: string; value: string }).value
        } else {
          // Handle array of strings (fallback)
          value = newValue[0] as string
        }
      }
    } else {
      // Handle single string value (fallback)
      value = newValue as string
    }

    setInappValue(value)
    updateUrl({ inapp: value })
  }

  function handleAuthenticableChange(newValue: boolean) {
    setAuthenticable(newValue)
    updateUrl({ authenticable: newValue })
  }

  const isAuthenticable = authenticable && !!urlValue

  // Find the matching option object for the current inapp value
  const selectedOption = urlOptions.find(option => option.value === inappValue)
  const selectValue = selectedOption ? [selectedOption] : []

  return (
    <>
      {!fixedUrlType && (
        <RadioGroup
          name={`${id}.url.type`}
          align="horizontal"
          value={urlType}
          onChange={value => handleUrlTypeChange(value)}
          disabled={disabled}
        >
          {!required && (
            <Radio className="mr-s" value={UrlType.NoLink}>
              {t('no_link')}
            </Radio>
          )}
          <Radio className="mr-s" value={UrlType.External}>
            {t('url')}
          </Radio>
          <Radio value={UrlType.Inapp}>{t('page_within')}</Radio>
        </RadioGroup>
      )}
      <Switch condition={urlType === UrlType.External}>
        <Switch
          condition={isAuthenticable}
          fallback={
            <Input
              name={`${id}.url.external`}
              value={urlValue}
              onChange={handleExternalUrlChange}
              placeholder="Enter URL"
              disabled={disabled}
            />
          }
        >
          <FakeInput
            text={getUrlValue(urlValue, isAuthenticable).external}
            disabled
            button={t('remove_link')}
            clickPassthrough={removeLink}
          />
          <Input
            className="hidden"
            name={`${id}.url.external`}
            value={fullUrl}
            onChange={handleExternalUrlChange}
            disabled={disabled}
          />
        </Switch>
      </Switch>
      <Switch condition={urlType === UrlType.Inapp}>
        <Select
          value={selectValue}
          onChange={handleInappUrlChange}
          options={urlOptions}
          disabled={disabled}
        />
      </Switch>
      <Input
        className="hidden"
        name={`${id}.url.authenticable`}
        value={authenticable.toString()}
        onChange={value => handleAuthenticableChange(value === 'true')}
        disabled={disabled}
      />
      <Switch condition={urlType === UrlType.External}>
        <div className="d-flex flex-column">
          <Switch
            condition={!isAuthenticable}
            fallback={
              <p className="mt-m p-0 float-left font-size-12 belize-hole-50">
                {t('advanced')}
              </p>
            }
          >
            <StyleableButton
              className="mt-m p-0 float-left font-size-12 belize-hole-50 text-left"
              onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            >
              {t('advanced')}
              <i
                className={`fa ${
                  isAdvancedOpen ? 'fa-caret-up' : 'fa-caret-down'
                } ml-xs`}
              />
            </StyleableButton>
          </Switch>
          <Switch condition={isAdvancedOpen || isAuthenticable}>
            <p
              className="body-text-5 mt-xxs float-left grey-70 text-left"
              dangerouslySetInnerHTML={{
                __html: isAuthenticable
                  ? t('advanced_link_enabled')
                  : t('advanced_link_disabled'),
              }}
            />
          </Switch>
        </div>
      </Switch>
    </>
  )
}

export default UrlPickerGeneric
